import axios from "../../axios";
import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import assets from "../../assets/assets";
import { iconData } from "../../iconData";
import PdfViewer from "./PdfViewer";
import { IP } from "../../internalServer";

const CreateMcq = ({ nextQuestion, question, setQuestions }) => {
  const options = question?.options || [];
  const correct_answers = question?.correct_answers || [];

  const [isValidFileType, setIsValidFileType] = useState(true);
  const [fLimit, setFLimit] = useState(false);
  const [loadingOptionIndex, setLoadingOptionIndex] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [fileIndex, setFileIndex] = useState(null);
  const [mideaviewer, setMideaviewer] = useState(false);
  const [responseAttach, setResponseAttach] = useState("");

  let teacher_id = JSON.parse(localStorage.getItem("Teacher_id"));

  const errorInputStyle = {
    border: "1px solid #FCA5A5",
    backgroundColor: "#FEF2F2",
  };
  const normalInputStyle = {
    border: "1px solid #D1D5DB",
    backgroundColor: "#fff",
  };

  // ==========================================
  // Options CRUD
  // ==========================================
  const addOptions = () => {
    setQuestions((prev) => {
      const currentOptions = prev[nextQuestion].options || [];
      const updatedOptions = [
        ...currentOptions,
        {
          key: String.fromCharCode(currentOptions.length + 65),
          value: null,
          attachment: null,
        },
      ];
      const updatedQuestion = {
        ...prev[nextQuestion],
        options: updatedOptions,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  const removeOptions = (index) => {
    setQuestions((prev) => {
      const currentQuestion = prev[nextQuestion];
      const currentOptions = currentQuestion.options || [];
      const updatedOptions = currentOptions
        .filter((_, idx) => idx !== index)
        .map((option, i) => ({
          ...option,
          key: String.fromCharCode(65 + i),
        }));

      const removedOptionKey = currentOptions[index]?.key;
      const currentCorrectAnswers = currentQuestion.correct_answers || [];
      const updatedCorrectAnswer = currentCorrectAnswers.map((ans) =>
        ans.option_key === removedOptionKey ? { ...ans, value: null } : ans,
      );

      const updatedQuestion = {
        ...currentQuestion,
        options: updatedOptions,
        correct_answers: updatedCorrectAnswer,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  const handleOptionChange = (index, value) => {
    setQuestions((prev) => {
      const currentOptions = prev[nextQuestion].options || [];
      const updatedOptions = currentOptions.map((opt, i) =>
        i === index ? { ...opt, value } : opt,
      );
      const updatedQuestion = {
        ...prev[nextQuestion],
        options: updatedOptions,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  // ==========================================
  // Attachment upload (option-level)
  // ==========================================
  const validateFileType = (filePath) => {
    const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.pdf)$/i;
    if (
      !allowedExtensions.exec(filePath.name) ||
      question?.attachments?.length > 5
    ) {
      setIsValidFileType(false);
      return false;
    }
    setIsValidFileType(true);
    return true;
  };

  const handleAttachment = async (file, optionIndex) => {
    if (!file || file.length === 0) return;
    const maxSize = 10 * 1024 * 1024;
    setFileIndex(optionIndex);
    if (file.size > maxSize) {
      setFLimit(true);
      return;
    }

    if (!validateFileType(file)) return;

    try {
      setLoadingOptionIndex(optionIndex);
      const formData = new FormData();
      formData.append("name", file.name);
      formData.append("uploadQuestionAttachment", file);
      const res = await axios.post(
        `/api/v1/question/uploadAttachment/teacherId/${teacher_id}`,
        formData,
      );
      if (res?.data?.data) {
        const attachmentUrl = res.data.data;
        const attachmentSizeKb = Math.round(file.size / 1024);

        setQuestions((prev) => {
          const updated = [...prev];
          const currentOptions = updated[nextQuestion].options || [];
          const updatedOptions = [...currentOptions];
          updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            attachment: attachmentUrl,
            attachment_sizeKb: attachmentSizeKb,
          };
          updated[nextQuestion] = {
            ...updated[nextQuestion],
            is_question_edited: true,
            options: updatedOptions,
          };
          return updated;
        });
        setFLimit(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOptionIndex(null);
    }
  };

  // ==========================================
  // Remove attachment (soft vs hard delete)
  // ==========================================
  const checkRemoveAttachmentisFromLibrary = (idx, attachment) => {
    if (question.id) handleSoftDeleteAttachment(idx, attachment);
    else handleRemoveAttachment(idx, attachment);
  };

  const handleSoftDeleteAttachment = (idx, attachment) => {
    setQuestions((prev) => {
      const currentOptions = prev[nextQuestion].options || [];
      const updatedOptions = currentOptions.map((opt, i) =>
        i === idx ? { ...opt, attachment: null, attachment_sizeKb: null } : opt,
      );
      const updatedQuestion = {
        ...prev[nextQuestion],
        options: updatedOptions,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  const handleRemoveAttachment = async (optionIndex, attachmentPath) => {
    try {
      const res = await axios.delete(`/api/v1/file/delete`, {
        data: { file_path: attachmentPath },
      });
      if (res) {
        setQuestions((prev) => {
          const currentOptions = prev[nextQuestion].options || [];
          const updatedOptions = currentOptions.map((opt, i) =>
            i === optionIndex
              ? { ...opt, attachment: null, attachment_sizeKb: null }
              : opt,
          );
          const updatedQuestion = {
            ...prev[nextQuestion],
            options: updatedOptions,
            is_question_edited: true,
          };
          const newQ = [...prev];
          newQ[nextQuestion] = updatedQuestion;
          return newQ;
        });
      }
    } catch (error) {
      console.error("Failed to delete attachment:", error);
    }
  };

  // ==========================================
  // Correct answers CRUD
  // ==========================================
  const addMoreCorrectAnswers = () => {
    setQuestions((prev) => {
      const currentAnswers = prev[nextQuestion].correct_answers || [];
      const updatedCorrectAnswer = [
        ...currentAnswers,
        {
          option_key: null,
          value: null,
          reason_for_answer: { explanation: null },
        },
      ];
      const updatedQuestion = {
        ...prev[nextQuestion],
        correct_answers: updatedCorrectAnswer,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  const removeMoreCorrectAnswers = (index) => {
    setQuestions((prev) => {
      const currentAnswers = prev[nextQuestion].correct_answers || [];
      const updatedCorrectAnswer = currentAnswers
        .filter((_, idx) => idx !== index)
        .map((cAnswers) => ({
          ...cAnswers,
          reason_for_answer: {
            explanation: cAnswers.reason_for_answer?.explanation || null,
          },
        }));
      const updatedQuestion = {
        ...prev[nextQuestion],
        correct_answers: updatedCorrectAnswer,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  const handleCorrectAnswerChange = (index, value) => {
    setQuestions((prev) => {
      const currentOptions = prev[nextQuestion].options || [];
      const currentCorrectAnswers = prev[nextQuestion].correct_answers || [];
      const optionValue = currentOptions.find(
        (opt) => opt.key === value,
      )?.value;

      const updatedCorrectAnswer = currentCorrectAnswers.map((ans, i) =>
        i === index ? { ...ans, option_key: value, value: optionValue } : ans,
      );
      const updatedQuestion = {
        ...prev[nextQuestion],
        correct_answers: updatedCorrectAnswer,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  const handleReasonChange = (index, value) => {
    setQuestions((prev) => {
      const currentAnswers = prev[nextQuestion].correct_answers || [];
      const updatedCorrectAnswer = currentAnswers.map((ans, i) =>
        i === index
          ? { ...ans, reason_for_answer: { explanation: value } }
          : ans,
      );
      const updatedQuestion = {
        ...prev[nextQuestion],
        correct_answers: updatedCorrectAnswer,
        is_question_edited: true,
      };
      const newQ = [...prev];
      newQ[nextQuestion] = updatedQuestion;
      return newQ;
    });
  };

  const handleClickforpdf = (fileUrl) => {
    setResponseAttach(fileUrl);
    setMideaviewer(true);
  };

  return (
    <>
      <div className="mt-3 mb-2">
        <span
          className="poppins-semibold"
          style={{ fontSize: "13px", color: "#374151" }}
        >
          Answer options *{" "}
          <span style={{ fontWeight: "normal", color: "#6B7280" }}>
            — select the correct option
          </span>
        </span>
      </div>

      {options?.map((option, index) => {
        const isOptionMissing = !option.value || option.value.trim() === "";
        return (
          <div
            key={index}
            className="d-flex align-items-center mb-2 gap-2 w-100"
          >
            <input
              type="radio"
              name={`correct_option_${nextQuestion}`}
              checked={correct_answers?.[0]?.option_key === option.key}
              onChange={() => handleCorrectAnswerChange(0, option.key)}
              style={{
                cursor: "pointer",
                accentColor: "#F28C28",
                transform: "scale(1.1)",
              }}
            />

            <span
              className="poppins-semibold"
              style={{ fontSize: "13px", color: "#4B5563", width: "20px" }}
            >
              {option.key}
            </span>

            <textarea
              className="rounded-2 p-2"
              rows="1"
              placeholder={`Enter option ${option.key}`}
              value={option.value || ""}
              onChange={(e) => handleOptionChange(index, e.target.value)}
              style={{
                flex: 1,
                fontSize: "13px",
                outline: "none",
                ...(isOptionMissing ? errorInputStyle : normalInputStyle),
                resize: "none",
                minHeight: "36px",
              }}
            />

            <div className="d-flex align-items-center gap-2">
              {/* Upload button — sirf tab jab attachment na ho */}
              {!option.attachment && (
                <>
                  <input
                    type="file"
                    id={`attachOption${nextQuestion}-${index}`}
                    style={{ display: "none" }}
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleAttachment(e.target.files[0], index)}
                  />
                  <label
                    htmlFor={`attachOption${nextQuestion}-${index}`}
                    style={{
                      border: "1px dashed #D1D5DB",
                      backgroundColor: "#F9FAFB",
                      cursor: "pointer",
                      width: "32px",
                      height: "32px",
                    }}
                    className="rounded-2 d-flex align-items-center justify-content-center"
                  >
                    <img src={assets.UploadIcon} width="16px" alt="Attach" />
                  </label>
                </>
              )}

              {loadingOptionIndex === index && (
                <img
                  style={{ width: "20px", height: "20px" }}
                  src="/gif/Loader.gif"
                  alt="Loading"
                />
              )}

              {/* 👈 ATTACHMENT PREVIEW — purani functionality */}
              {option.attachment &&
                (() => {
                  const fileUrl =
                    typeof option.attachment === "string"
                      ? option.attachment
                      : option.attachment?.url;
                  if (!fileUrl) return null;

                  const fileType = fileUrl.split(".").pop().toLowerCase();
                  const isImage = ["jpg", "jpeg", "png"].includes(fileType);
                  const isVideo = ["mp4", "mov"].includes(fileType);
                  const isPdf = fileType === "pdf";
                  const fileIcon =
                    iconData?.find((i) => i.extension === fileType)?.icon ||
                    "/images/file_icon.png";
                  const fullUrl = fileUrl.startsWith("http")
                    ? fileUrl
                    : `${IP}${fileUrl}`;
                  const fileName = fileUrl.replace(/^[^_]*_/, "");

                  return (
                    <div
                      style={{
                        position: "relative",
                        borderRadius: "6px",
                        border: "1px solid #E5E7EB",
                        backgroundColor: "#fff",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {/* Image */}
                      {isImage ? (
                        <img
                          src={fullUrl}
                          alt="attachment"
                          onClick={() => setZoomedImage(fullUrl)}
                          style={{
                            width: "56px",
                            height: "44px",
                            objectFit: "cover",
                            cursor: "pointer",
                            display: "block",
                          }}
                        />
                      ) : isVideo ? (
                        <video
                          src={fullUrl}
                          style={{
                            width: "64px",
                            height: "44px",
                            objectFit: "cover",
                            display: "block",
                          }}
                          controls
                        />
                      ) : isPdf ? (
                        <div
                          onClick={() => handleClickforpdf(fileUrl)}
                          style={{
                            width: "72px",
                            height: "44px",
                            background: "#f5f5f5",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "2px",
                          }}
                          title={fileName}
                        >
                          <img
                            src={fileIcon}
                            alt="pdf"
                            style={{ width: "18px", height: "18px" }}
                          />
                          <span
                            style={{
                              fontSize: "8px",
                              color: "#6B7280",
                              maxWidth: "68px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fileName}
                          </span>
                        </div>
                      ) : (
                        <a
                          href={`https://docs.google.com/gview?url=${encodeURIComponent(
                            fullUrl,
                          )}&embedded=true`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none" }}
                        >
                          <div
                            style={{
                              width: "72px",
                              height: "44px",
                              background: "#f5f5f5",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "2px",
                            }}
                            title={fileName}
                          >
                            <img
                              src={fileIcon}
                              alt="file"
                              style={{ width: "18px", height: "18px" }}
                            />
                            <span
                              style={{
                                fontSize: "8px",
                                color: "#6B7280",
                                maxWidth: "68px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fileName}
                            </span>
                          </div>
                        </a>
                      )}

                      {/* Remove (×) */}
                      <img
                        onClick={() =>
                          checkRemoveAttachmentisFromLibrary(
                            index,
                            option.attachment,
                          )
                        }
                        className="attachment_cross_icon"
                        src="/images/attachment_cross_icon.png"
                        alt="Remove"
                        style={{
                          position: "absolute",
                          top: "2px",
                          right: "2px",
                          cursor: "pointer",
                          width: "12px",
                          height: "12px",
                          backgroundColor: "rgba(255,255,255,0.9)",
                          borderRadius: "50%",
                          padding: "1px",
                        }}
                      />
                    </div>
                  );
                })()}

              {/* Delete option */}
              {options.length > 1 && (
                <button
                  onClick={() => removeOptions(index)}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: "4px",
                  }}
                  title="Remove option"
                >
                  <Trash2 size={16} color="#DC2626" />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {fLimit && (
        <p style={{ fontSize: "10px", color: "red", marginBottom: "4px" }}>
          Max file size: 10 MB
        </p>
      )}
      {!isValidFileType && (
        <p style={{ fontSize: "10px", color: "red", marginBottom: "4px" }}>
          Invalid file type. Allowed: pdf, jpg, jpeg, png.
        </p>
      )}

      <div className="d-flex mt-1 mb-3">
        <button
          onClick={addOptions}
          style={{
            border: "1px dashed #F28C28",
            color: "#C2410C",
            backgroundColor: "#FFF7ED",
            fontSize: "12px",
            padding: "6px 12px",
          }}
          className="rounded-2 d-flex align-items-center gap-1 poppins-semibold"
        >
          <img src="/images/add button.svg" alt="" style={{ width: 12 }} /> Add
          Option
        </button>
      </div>

      <div className="mb-2 mt-3">
        <span
          className="poppins-semibold"
          style={{ fontSize: "13px", color: "#374151" }}
        >
          Correct Answer & Reason *
        </span>
      </div>

      {correct_answers?.map((cOption, index) => {
        const isAnswerMissing = !cOption.option_key;
        return (
          <div
            key={index}
            className="d-flex align-items-center gap-2 mb-2 w-100"
          >
            <select
              onChange={(e) => handleCorrectAnswerChange(index, e.target.value)}
              value={cOption.option_key || ""}
              className="form-select rounded-2"
              style={{
                width: "80px",
                fontSize: "13px",
                ...(isAnswerMissing ? errorInputStyle : normalInputStyle),
                padding: "6px",
              }}
            >
              <option value="">Key</option>
              {options.map((option, i) => (
                <option key={i} value={option.key}>
                  {option.key}
                </option>
              ))}
            </select>

            <input
              onChange={(e) => handleReasonChange(index, e.target.value)}
              className="rounded-2 p-2"
              style={{
                flex: 1,
                fontSize: "13px",
                ...normalInputStyle,
                outline: "none",
                minHeight: "36px",
              }}
              placeholder="Reason / Explanation for answer..."
              value={cOption.reason_for_answer?.explanation || ""}
            />

            {correct_answers.length > 1 && (
              <button
                onClick={() => removeMoreCorrectAnswers(index)}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: "4px",
                }}
                title="Remove answer"
              >
                <Trash2 size={16} color="#DC2626" />
              </button>
            )}
          </div>
        );
      })}

      <div className="d-flex mt-1 mb-2">
        <button
          onClick={addMoreCorrectAnswers}
          style={{
            border: "1px dashed #F28C28",
            color: "#C2410C",
            backgroundColor: "#FFF7ED",
            fontSize: "12px",
            padding: "6px 12px",
          }}
          className="rounded-2 d-flex align-items-center gap-1 poppins-semibold"
        >
          <img src="/images/add button.svg" alt="" style={{ width: 12 }} /> Add
          Correct Answer
        </button>
      </div>

      {mideaviewer && (
        <PdfViewer
          setMideaviewer={setMideaviewer}
          attachment={responseAttach}
        />
      )}

      {zoomedImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <button
            onClick={() => setZoomedImage(null)}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              background: "transparent",
              border: "none",
              fontSize: "2rem",
              color: "white",
              cursor: "pointer",
            }}
          >
            &times;
          </button>
          <img
            src={zoomedImage}
            alt="Zoomed"
            style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: "8px" }}
          />
        </div>
      )}
    </>
  );
};

export default CreateMcq;
